<template>
    <div class="modal-overlay" @click="closeModal">
        <div class="modal-content" @click.stop>
            <div class="modal-header">
                <h3>基金消耗</h3>
                <button class="close-btn" @click="closeModal">×</button>
            </div>
            <div class="modal-body">
                <div class="form-group">
                    <label for="user-select">选择用户:</label>
                    <select id="user-select" v-model="selectedUserId" @change="loadUserWorth">
                        <option value="">请选择用户</option>
                        <option v-for="user in users" :key="user.id" :value="user.id">
                            {{ user.name }}
                        </option>
                    </select>
                </div>

                <div v-if="selectedUserWorth !== null" class="worth-info">
                    <p>当前成长基金: ¥{{ selectedUserWorth.toFixed(2) }}</p>
                </div>

                <div class="form-group">
                    <label for="deduction-amount">扣除金额:</label>
                    <input id="deduction-amount" type="number" v-model="deductionAmount" placeholder="请输入扣除金额" min="0"
                        step="0.01" />
                </div>

                <div class="form-group">
                    <label for="deduction-reason">扣除原因:</label>
                    <textarea id="deduction-reason" v-model="deductionReason" placeholder="请输入扣除原因" rows="3"></textarea>
                </div>

                <div class="form-actions">
                    <button class="btn secondary" @click="closeModal">取消</button>
                    <button class="btn primary" @click="submitDeduction" :disabled="!canSubmit">扣除</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import TodoService from '@/services/todoService.js'
import { useTodoStore } from '../stores/todoStore.js'
import AuthService from '@/services/authService.js'

export default {
    name: 'FundDeductionModal',
    emits: ['close'],
    setup(props, { emit }) {
        // 获取store实例
        const store = useTodoStore()

        // 获取当前用户信息
        const currentUser = AuthService.getCurrentUser();

        // 用户列表
        const users = ref([])

        // 选中的用户ID
        const selectedUserId = ref('')

        // 选中用户的成长基金余额
        const selectedUserWorth = ref(null)

        // 扣除金额
        const deductionAmount = ref('')

        // 扣除原因
        const deductionReason = ref('')

        // 加载所有用户信息
        const loadUsers = async () => {
            try {
                const response = await TodoService.getAllUsers();
                if (response.status === 'ok') {
                    users.value = response.users;
                } else {
                    console.error('加载用户列表失败:', response.message);
                    // 出错时使用模拟数据
                    users.value = [
                        { id: 1, name: '用户1' },
                        { id: 2, name: '用户2' },
                        { id: 3, name: '用户3' }
                    ];
                }
            } catch (error) {
                console.error('加载用户列表失败:', error);
                // 出错时使用模拟数据
                users.value = [
                    { id: 1, name: '用户1' },
                    { id: 2, name: '用户2' },
                    { id: 3, name: '用户3' }
                ];
            }
        }

        // 加载选中用户的成长基金余额
        const loadUserWorth = async () => {
            if (!selectedUserId.value) {
                selectedUserWorth.value = null
                return
            }

            try {
                const response = await TodoService.getUserWorthById(selectedUserId.value);
                if (response.status === 'ok') {
                    selectedUserWorth.value = response.current_worth;
                } else {
                    console.error('加载用户成长基金失败:', response.message);
                    selectedUserWorth.value = null;
                }
            } catch (error) {
                console.error('加载用户成长基金失败:', error);
                selectedUserWorth.value = null;
            }
        }

        // 关闭模态框
        const closeModal = () => {
            emit('close')
        }

        // 是否可以提交
        const canSubmit = computed(() => {
            return selectedUserId.value &&
                deductionAmount.value &&
                parseFloat(deductionAmount.value) > 0 &&
                deductionReason.value.trim()
        })

        // 提交扣除操作
        const submitDeduction = async () => {
            if (!canSubmit.value) return

            try {
                const response = await TodoService.deductUserWorth(
                    selectedUserId.value,
                    parseFloat(deductionAmount.value),
                    deductionReason.value
                );

                if (response.status === 'ok') {
                    // 显示成功消息
                    alert('基金扣除成功!');

                    // 重新加载用户基金余额
                    await loadUserWorth();

                    // 检查是否是对当前用户进行的操作，如果是则更新store中的用户价值
                    if (currentUser && parseInt(selectedUserId.value) === parseInt(currentUser.userid)) {
                        try {
                            // 重新获取当前用户的基金余额并更新store
                            const worthResponse = await TodoService.getUserWorth();
                            if (worthResponse.status === 'ok') {
                                store.updateUserWorth(worthResponse.current_worth);
                            }
                        } catch (error) {
                            console.error('更新当前用户基金余额失败:', error);
                        }
                    }

                    // 清空输入
                    deductionAmount.value = '';
                    deductionReason.value = '';
                } else {
                    console.error('基金扣除失败:', response.message);
                    alert('基金扣除失败: ' + response.message);
                }
            } catch (error) {
                console.error('基金扣除失败:', error);
                alert('基金扣除失败: ' + error.message);
            }
        }

        // 组件挂载时加载用户列表
        onMounted(() => {
            loadUsers()
        })

        return {
            users,
            selectedUserId,
            selectedUserWorth,
            deductionAmount,
            deductionReason,
            loadUserWorth,
            closeModal,
            canSubmit,
            submitDeduction
        }
    }
}
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    border-radius: 12px;
    width: 100%;
    max-width: 500px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #eee;
}

.modal-header h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
}

.close-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #999;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.close-btn:hover {
    color: #333;
}

.modal-body {
    padding: 20px;
}

.form-group {
    margin-bottom: 20px;
}

.form-group label {
    display: block;
    margin-bottom: 5px;
    font-weight: 500;
    color: #333;
}

.form-group select,
.form-group input,
.form-group textarea {
    width: 100%;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 16px;
    box-sizing: border-box;
}

.form-group textarea {
    resize: vertical;
}

.worth-info {
    background: #e8f4f8;
    padding: 15px;
    border-radius: 6px;
    margin-bottom: 20px;
}

.worth-info p {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #2c3e50;
}

.form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 30px;
}

.btn {
    padding: 10px 20px;
    border: none;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
}

.btn.primary {
    background: #e74c3c;
    color: white;
}

.btn.primary:hover:not(:disabled) {
    background: #c0392b;
}

.btn.primary:disabled {
    background: #bdc3c7;
    cursor: not-allowed;
}

.btn.secondary {
    background: #f0f0f0;
    color: #333;
}

.btn.secondary:hover {
    background: #e0e0e0;
}
</style>